/**
 * Typed HTTP client.
 *
 * Every API call goes through `apiFetch`. It enforces the conventions from
 * `API_CONTRACT.md`:
 *   - `credentials: "include"` — the session travels as `HttpOnly` cookies the
 *     API owns. This client never sees, stores, or sends a token.
 *   - `X-CSRF-Token` on every mutation, read from the session bridge. The
 *     cookie is ambient, so the CSRF token is what distinguishes a request our
 *     own script made from one a cross-site page made with the same cookie.
 *   - `X-Request-Id` on every request (uuid; echoed in audit + errors)
 *   - `X-Reason` on audit-critical mutations (per request)
 *   - `Idempotency-Key` on every non-GET (per request, auto-generated)
 *   - Response parsed through a zod schema → CONTRACT_VIOLATION on mismatch
 *   - Error envelope parsed → ApiError with the backend's code preserved
 *   - One silent retry on 401/TOKEN_EXPIRED after refreshing the session
 *
 * Components do not call apiFetch directly — they go through TanStack
 * Query hooks (Stage 5: `useBatches()`, `useBatch()`, …) so caching,
 * deduping, and background refetch happen automatically.
 */

import { ACCOUNT_DENIAL_CODES, ApiError, type ApiErrorEnvelope } from "@compliance/domain";
import type { z } from "zod";
import {
  getCsrfToken,
  isAuthenticated,
  refreshSessionViaBridge,
  reportSessionDenialViaBridge,
  resyncSessionViaBridge,
} from "../auth/sessionBridge";
import { errorEnvelopeFrom, isSettledSessionRefusal } from "./errors";
import { readOrgSelection } from "./orgSelection";

/**
 * The 401 codes that mean "your access cookie lapsed", as opposed to "you were
 * never signed in".
 *
 * UNAUTHENTICATED is the *common* one, which is not obvious. The access cookie's
 * `Max-Age` is the access token's own lifetime, so the browser drops the cookie
 * at the moment the token expires and the API sees no cookie at all — "Missing
 * session cookie", not "expired". TOKEN_EXPIRED covers only the millisecond
 * window where the cookie outlives the token inside it.
 *
 * Reading UNAUTHENTICATED as final is what would sign every user out one hour
 * after login, despite a refresh cookie good for fourteen days.
 */
const REFRESHABLE_401_CODES = new Set(["TOKEN_EXPIRED", "UNAUTHENTICATED"]);

/**
 * Should a 401 be met with one refresh and a retry?
 *
 * Gated on the session we believe we hold: an anonymous caller gets the same
 * UNAUTHENTICATED, and refreshing on that would add a doomed round trip to
 * every request made before sign-in.
 */
/**
 * A failed refresh must not be reported as the 401 that provoked it.
 *
 * If the refresh could not be *answered* — a 5xx, a network drop, a timeout, or a
 * 429 that means "ask again shortly" — then nothing has been learned about the
 * session, and surfacing the original 401 tells `AuthProvider` the opposite: 401
 * is its settled auth failure, so it clears a session whose refresh cookie may be
 * good for another two weeks. Re-throwing the transient error instead routes the
 * caller down its retry path.
 *
 * A settled refusal is different: the API has ruled on the refresh cookie, and
 * the original 401 is then the honest answer to return.
 */
function refreshFailureToSurface(err: unknown, original: ApiError): unknown {
  return isSettledSessionRefusal(err) ? original : err;
}

/**
 * Should a 403 be met with one session re-read and a retry?
 *
 * A rotated CSRF token is the one 403 worth retrying. Two tabs share a session
 * but not a bridge, so either tab's refresh leaves the other holding a token the
 * API no longer accepts — and without this its every mutation fails until a
 * reload, since the 401 refresh path never fires for a 403.
 */
async function shouldResyncAndRetry(response: Response): Promise<boolean> {
  if (response.status !== 403 || !isAuthenticated()) return false;
  return (await peekErrorCode(response)) === "CSRF_FAILED";
}

async function shouldRefreshAndRetry(response: Response): Promise<boolean> {
  if (response.status !== 401 || !isAuthenticated()) return false;
  const code = await peekErrorCode(response);
  const refreshable = code !== null && REFRESHABLE_401_CODES.has(code);
  if (refreshable) announceRefresh(code, response.url);
  return refreshable;
}

/**
 * Say out loud that a renewal happened.
 *
 * This path is close to unobservable after the fact: it leaves no UI trace by
 * design, and the 401 it recovers from looks like any other in a network log
 * unless you spot the retry beside it. Confirming it in a deployed environment
 * once cost two hour-long waits and still produced no direct evidence, so the
 * code now reports itself.
 *
 * `console.debug` is hidden at Chrome's default log level: free for anyone who
 * opens the Verbose tab, invisible to everyone else. The route *shape* only —
 * see `redactIds`. Dropping the query string alone was the first cut of this and
 * was not enough: identifiers live in the path too.
 */
function announceRefresh(code: string, url: string): void {
  let path = "";
  try {
    path = redactIds(new URL(url).pathname);
  } catch {
    // Relative or malformed: the code alone is still worth saying.
  }
  console.debug(`[auth] ${code}${path ? ` on ${path}` : ""} — refreshing the session and retrying`);
}

/**
 * Replace identifier-bearing path segments with `:id`.
 *
 * Dropping the query string is not enough: identifiers live in the path too.
 * `PATCH /users/{uuid}/role` and `/batches/{batch-id}/reports` both name a
 * record, and a console line is a diagnostic channel like any other — consoles
 * get screenshotted into tickets and swept up by whatever session tooling
 * arrives later. This repo's rule is that identifiers stay out of client
 * diagnostics, so the endpoint *shape* is what gets logged, not which record it
 * touched.
 *
 * The rule is deliberately blunt: every route segment in this API is a lowercase
 * word (`batches`, `users`, `reports`, `signed-url`), and every id carries a
 * digit — UUIDs, `2177-73241-26-0004`, numeric keys. So "contains a digit" is a
 * safe over-approximation. It also collapses a segment like `rfs4800` that is
 * really a report name, which costs a little precision and risks nothing.
 */
function redactIds(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => (/\d/.test(segment) ? ":id" : segment))
    .join("/");
}

/**
 * Base URL for the compliance API. MSW intercepts `fetch()` at the request
 * level so this URL is also what MSW handlers register against.
 *
 * In dev: defaults to the local FastAPI dev server on localhost:8000.
 * In prod: VITE_API_BASE_URL MUST be set explicitly — we refuse to fall
 * back to localhost because a misconfigured prod build would silently
 * call the visitor's own machine.
 *
 * The `localhost` spelling is load-bearing, not cosmetic. This app is served at
 * `http://localhost:5174`, and the session cookies are `SameSite=Lax`, which a
 * browser withholds from a cross-*site* subrequest. `localhost` and `127.0.0.1`
 * are different sites, so calling the API by IP from a page served by name means
 * every request arrives with no session cookie at all — an auth failure that
 * looks like the login not working rather than like a URL mismatch.
 */
function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv) return fromEnv;
  if (import.meta.env.PROD) {
    throw new Error(
      "VITE_API_BASE_URL is required in production builds. " +
        "Set it explicitly (e.g. https://api.example.com/api/v2/compliance). " +
        "Refusing to default to localhost in a deployed build.",
    );
  }
  return "http://localhost:8000/api/v2/compliance";
}

/**
 * Base URL for the shared browser-auth API — session bootstrap, login,
 * refresh, and logout. A *different* mounted sub-app from the compliance API,
 * so it gets its own variable rather than being derived from the one above by
 * string surgery: the two are independent deploy-time facts, and a silent
 * `.replace("/compliance", "/auth")` would fail unreadably the first time an
 * environment mounts them on different hosts.
 */
function resolveAuthBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_AUTH_BASE_URL;
  if (fromEnv) return fromEnv;
  if (import.meta.env.PROD) {
    throw new Error(
      "VITE_AUTH_BASE_URL is required in production builds. " +
        "Set it explicitly (e.g. https://api.example.com/api/v2/auth). " +
        "Refusing to default to localhost in a deployed build.",
    );
  }
  return "http://localhost:8000/api/v2/auth";
}

export const BASE_URL = resolveBaseUrl();
export const AUTH_BASE_URL = resolveAuthBaseUrl();

export type ApiMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export type ApiFetchOptions<TBody = unknown> = {
  method?: ApiMethod;
  /** JSON body for mutations. Serialised with JSON.stringify. */
  body?: TBody;
  /** Sets X-Reason for audit-critical mutations (per API_CONTRACT §10). */
  reason?: string;
  /**
   * Custom idempotency key. Defaults to a fresh uuid for non-GET requests.
   * Pass a stable key to make a mutation safely retryable across reloads.
   */
  idempotencyKey?: string;
  /** Aborts the request when this signal fires (TanStack Query owns it). */
  signal?: AbortSignal;
  /**
   * Client-side timeout in milliseconds. When set, the request is aborted after
   * this long and surfaces as a `TIMEOUT` ApiError instead of hanging forever —
   * the guard for endpoints that fan out to slow/unreachable upstreams (e.g.
   * `POST /batches/{id}/validate` reading the per-facility DUT server). Composes
   * with `signal`: a user cancel and the timeout race, whichever fires first.
   */
  timeout?: number;
  /** Extra query params (object → URLSearchParams). */
  query?: Record<string, string | number | boolean | undefined>;
};

/**
 * Typed fetch wrapper.
 *
 * @param path  Absolute (`http://...`) or relative (`/batches/...`) URL.
 * @param schema  zod schema for the success response — runtime validated.
 * @param options  Method, body, reason, idempotency key, etc.
 */
export async function apiFetch<TResponse>(
  path: string,
  schema: z.ZodType<TResponse>,
  options: ApiFetchOptions = {},
): Promise<TResponse> {
  const url = buildUrl(path, options.query);
  const method = options.method ?? "GET";
  const isMutation = method !== "GET";

  // Idempotency-Key represents one user intent. The contract requires the
  // SAME key on every retry of the same intent, so we materialize it once
  // here and reuse it for the silent TOKEN_EXPIRED retry below.
  const idempotencyKey = isMutation ? (options.idempotencyKey ?? crypto.randomUUID()) : undefined;

  const initialHeaders = buildHeaders({
    csrfToken: isMutation ? getCsrfToken() : null,
    reason: options.reason,
    idempotencyKey,
  });

  // A client-side timeout races the caller's own signal (TanStack cancel). The
  // timeout signal is kept separate so we can tell a timeout apart from a user
  // cancel in the catch below and surface a friendly error only for the former.
  const timeoutSignal =
    options.timeout && options.timeout > 0 ? AbortSignal.timeout(options.timeout) : undefined;

  const init: RequestInit = {
    method,
    headers: initialHeaders,
    credentials: "include",
    signal: combineSignals(options.signal, timeoutSignal),
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    throw mapAbortError(err, timeoutSignal);
  }

  // Silently refresh once when the access cookie has lapsed, so a one-hour
  // token is invisible to a tab left open across it.
  if (await shouldRefreshAndRetry(response)) {
    try {
      const refreshed = await refreshSessionViaBridge();
      const retryHeaders = buildHeaders({
        // A refresh mints a new CSRF token; reusing the stale one would turn
        // the retry into a 403 CSRF_FAILED.
        csrfToken: isMutation ? refreshed.csrfToken : null,
        reason: options.reason,
        // Same key as the first attempt — that's what makes this a safe
        // retry under the contract's 24h idempotency window.
        idempotencyKey,
      });
      response = await fetch(url, { ...init, headers: retryHeaders });
    } catch (err) {
      // The refresh failed. Which error the caller sees decides whether the
      // session survives — see refreshFailureToSurface.
      throw refreshFailureToSurface(err, await buildApiError(response));
    }
  }

  // A rotated CSRF token, most likely because another tab refreshed. One
  // re-read, then the same request with the same idempotency key.
  if (isMutation && (await shouldResyncAndRetry(response))) {
    try {
      const resynced = await resyncSessionViaBridge();
      response = await fetch(url, {
        ...init,
        headers: buildHeaders({
          csrfToken: resynced.csrfToken,
          reason: options.reason,
          idempotencyKey,
        }),
      });
    } catch {
      // Fall through with the original 403.
    }
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  // 204 No Content — return void-cast for callers whose schema is z.void()
  // or undefined.
  if (response.status === 204) {
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) {
      throw contractViolation(response, parsed.error);
    }
    return parsed.data;
  }

  const json = await response.json();
  // For paginated list endpoints the BE returns a bare array body and
  // puts the cursor in the X-Next-Cursor response header. ONLY wrap
  // when that header is present — otherwise we'd wrap every bare-array
  // response (calc-results, calibration-due, etc.) and break schemas
  // that expect `z.array(...)` rather than the `{items, next_cursor}`
  // envelope. List schemas accept both shapes via their preprocess.
  const nextCursor = response.headers.get("X-Next-Cursor");
  const toParse =
    Array.isArray(json) && nextCursor !== null ? { items: json, next_cursor: nextCursor } : json;
  const parsed = schema.safeParse(toParse);
  if (!parsed.success) {
    throw contractViolation(response, parsed.error);
  }
  return parsed.data;
}

/**
 * Variant of apiFetch for endpoints that return raw bytes (CSV streams,
 * binaries, etc.) instead of JSON. Auth + headers identical to apiFetch;
 * returns a Blob plus the parsed `Content-Disposition` filename.
 *
 * Implements the same TOKEN_EXPIRED silent-retry as `apiFetch` so blob
 * downloads (e.g. /audit/export) survive token rotation without surfacing
 * a 401 to the caller.
 */
export async function apiFetchBlob(
  path: string,
  options: ApiFetchOptions = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const url = buildUrl(path, options.query);
  const method = options.method ?? "GET";
  const isMutation = method !== "GET";

  // Materialize idempotency key once so the retry below reuses it.
  const idempotencyKey = isMutation ? (options.idempotencyKey ?? crypto.randomUUID()) : undefined;

  // Re-establish apiFetch's timeout + abort mapping (BIO-144 #13): without it a
  // wedged S3/signing upstream hangs the download spinner forever and a network
  // drop throws a raw TypeError instead of a normalized ApiError.
  const timeoutSignal =
    options.timeout && options.timeout > 0 ? AbortSignal.timeout(options.timeout) : undefined;
  const signal = combineSignals(options.signal, timeoutSignal);

  const buildInit = (csrfToken: string | null): RequestInit => ({
    method,
    headers: buildHeaders({ csrfToken, reason: options.reason, idempotencyKey }),
    credentials: "include",
    signal,
  });

  let response: Response;
  try {
    response = await fetch(url, buildInit(isMutation ? getCsrfToken() : null));
  } catch (err) {
    throw mapAbortError(err, timeoutSignal);
  }

  if (await shouldRefreshAndRetry(response)) {
    try {
      const refreshed = await refreshSessionViaBridge();
      response = await fetch(url, buildInit(isMutation ? refreshed.csrfToken : null));
    } catch (err) {
      // A fired timeout/abort must surface as such, not be swallowed as a
      // failed refresh.
      if (timeoutSignal?.aborted || options.signal?.aborted) {
        throw mapAbortError(err, timeoutSignal);
      }
      throw refreshFailureToSurface(err, await buildApiError(response));
    }
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  const match = disposition?.match(/filename="?([^";]+)"?/);
  return { blob, filename: match?.[1] ?? null };
}

/**
 * Variant of apiFetch for multipart/form-data uploads. The browser sets
 * the boundary automatically when `body` is a FormData; do NOT add a
 * Content-Type header ourselves.
 *
 * Same auth + retry semantics as apiFetch. Returns the parsed JSON
 * response validated against the supplied schema.
 *
 * Note: native `fetch` doesn't expose upload-progress events. L1-FE-01
 * accepts that — progress UI is deferred to a future ticket that may
 * swap to XHR or ReadableStream upload (L1-FE-02 polish).
 */
export async function apiFetchMultipart<TResponse>(
  path: string,
  formData: FormData,
  schema: z.ZodType<TResponse>,
  options: Omit<ApiFetchOptions, "body" | "method"> = {},
): Promise<TResponse> {
  const url = buildUrl(path, options.query);

  const idempotencyKey = options.idempotencyKey ?? crypto.randomUUID();
  const buildHeaders_ = (csrfToken: string | null): Headers => {
    const h = new Headers({
      "X-Request-Id": crypto.randomUUID(),
      "Idempotency-Key": idempotencyKey,
    });
    if (csrfToken) h.set("X-CSRF-Token", csrfToken);
    if (options.reason) h.set("X-Reason", reasonHeaderValue(options.reason));
    setOrganizationHeader(h);
    // Intentionally no Content-Type — fetch fills it with the boundary.
    return h;
  };

  // Same timeout + abort-mapping guard as apiFetch (BIO-144 #4): without it a
  // canceled/dropped upload throws a raw TypeError/AbortError that Uploads.tsx's
  // isApiError branch can't classify, so the duplicate/malware/validation
  // prompts never fire and there's no request_id correlation.
  const timeoutSignal =
    options.timeout && options.timeout > 0 ? AbortSignal.timeout(options.timeout) : undefined;
  const signal = combineSignals(options.signal, timeoutSignal);

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: buildHeaders_(getCsrfToken()),
      body: formData,
      credentials: "include",
      signal,
    });
  } catch (err) {
    throw mapAbortError(err, timeoutSignal);
  }

  if (await shouldRefreshAndRetry(response)) {
    try {
      const refreshed = await refreshSessionViaBridge();
      response = await fetch(url, {
        method: "POST",
        headers: buildHeaders_(refreshed.csrfToken),
        body: formData,
        credentials: "include",
        signal,
      });
    } catch (err) {
      if (timeoutSignal?.aborted || options.signal?.aborted) {
        throw mapAbortError(err, timeoutSignal);
      }
      throw refreshFailureToSurface(err, await buildApiError(response));
    }
  }

  // Uploads mutate, so they meet a rotated CSRF token the same way a JSON
  // mutation does — and without this an upload stays broken until a reload.
  if (await shouldResyncAndRetry(response)) {
    try {
      const resynced = await resyncSessionViaBridge();
      response = await fetch(url, {
        method: "POST",
        headers: buildHeaders_(resynced.csrfToken),
        body: formData,
        credentials: "include",
        signal,
      });
    } catch {
      // Fall through with the original 403.
    }
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  const json = await response.json();
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw contractViolation(response, parsed.error);
  }
  return parsed.data;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Merge the caller's abort signal with an optional timeout signal. */
function combineSignals(a?: AbortSignal, b?: AbortSignal): AbortSignal | undefined {
  if (a && b) return AbortSignal.any([a, b]);
  return a ?? b;
}

/**
 * Map a `fetch` rejection to the right error. A fired timeout becomes a
 * `TIMEOUT` ApiError (so the UI shows "server took too long" instead of
 * spinning forever); a user-initiated cancel or a plain network failure is
 * rethrown unchanged so TanStack Query treats it as a cancellation / network
 * error rather than a surfaced server error.
 */
function mapAbortError(err: unknown, timeoutSignal?: AbortSignal): unknown {
  if (timeoutSignal?.aborted) {
    return new ApiError(0, {
      code: "TIMEOUT",
      message: "The server took too long to respond. Please check connectivity and retry.",
      request_id: "unknown",
    });
  }
  return err;
}

function buildUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): string {
  const base = path.startsWith("http") ? path : `${BASE_URL}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}${base.includes("?") ? "&" : "?"}${qs}` : base;
}

/**
 * Name the organization this tab is working in, when one has been chosen.
 *
 * Sent on every request rather than only the ones that need it, because the
 * acting organization is a property of the session, not of the call — and the
 * backend re-validates it every time. Only reaches the compliance API: the auth
 * endpoints in `auth/session.ts` build their own headers and must not carry it.
 */
function setOrganizationHeader(h: Headers): void {
  const organizationId = readOrgSelection();
  if (organizationId) h.set("X-Organization-Id", organizationId);
}

function buildHeaders(opts: {
  /** Non-null only for mutations — the backend gates unsafe methods, not GETs. */
  csrfToken: string | null;
  reason?: string;
  idempotencyKey?: string;
}): Headers {
  const h = new Headers({
    "Content-Type": "application/json",
    // API_CONTRACT §5: X-Request-Id is a bare uuid (no prefix). The backend
    // echoes it on the response and in audit log rows for correlation.
    "X-Request-Id": crypto.randomUUID(),
  });
  if (opts.csrfToken) h.set("X-CSRF-Token", opts.csrfToken);
  if (opts.reason) h.set("X-Reason", reasonHeaderValue(opts.reason));
  if (opts.idempotencyKey) h.set("Idempotency-Key", opts.idempotencyKey);
  setOrganizationHeader(h);
  return h;
}

/**
 * `X-Reason`, encoded per RFC 8187 when it cannot travel as raw bytes.
 *
 * An HTTP header carries bytes, so `Headers.set()` throws a `TypeError` on any
 * character above U+00FF — and a line break fails header validation. That threw
 * **before the request was sent**, so an operator writing in Chinese, or ending
 * a sentence with an emoji, watched the action silently do nothing.
 *
 * Plain ASCII is sent as-is, so nothing changes for the overwhelming majority of
 * reasons and the wire stays readable. Anything else is percent-encoded UTF-8
 * behind the `UTF-8''` prefix — the same mechanism `Content-Disposition` uses for
 * non-ASCII filenames — which the API decodes. Encoding unconditionally would
 * make every audit header unreadable in a proxy log for no gain.
 */
export function reasonHeaderValue(reason: string): string {
  // eslint-disable-next-line no-control-regex
  const travels = /^[\x20-\x7E]*$/.test(reason);
  return travels ? reason : `UTF-8''${encodeURIComponent(reason)}`;
}

async function peekErrorCode(response: Response): Promise<string | null> {
  try {
    const cloned = response.clone();
    const json = await cloned.json();
    // Both shapes, for the same reason buildApiError accepts both: this is what
    // the retry decisions branch on, and a Problem Details body carries a real
    // code that a flat-envelope-only parse throws away.
    return errorEnvelopeFrom(json, requestIdOf(response))?.code ?? null;
  } catch {
    return null;
  }
}

/** The id the API echoed for this response, for correlating with its logs. */
function requestIdOf(response: Response): string {
  return response.headers.get("X-Request-Id") ?? "unknown";
}

async function buildApiError(response: Response): Promise<ApiError> {
  let envelope: ApiErrorEnvelope;
  try {
    const json = await response.json();
    envelope = errorEnvelopeFrom(json, requestIdOf(response)) ?? {
      code: "MALFORMED_ERROR",
      message: `Server returned ${response.status} but the error envelope was not parseable`,
      request_id: requestIdOf(response),
    };
  } catch {
    envelope = {
      code: response.status === 0 ? "NETWORK_ERROR" : "MALFORMED_ERROR",
      message: `Request failed: ${response.status} ${response.statusText}`,
      request_id: requestIdOf(response),
    };
  }
  const error = new ApiError(response.status, envelope);
  // Every error this client raises passes through here, which is what makes it the
  // right place to notice a refusal of the session rather than of the request. The
  // auth layer decides what to render; this only says what arrived.
  // `ORGANIZATION_SELECTION_REQUIRED` rides the same channel although it is not
  // a denial: it too refuses the session rather than the request, and it can
  // first appear on an ordinary data query — an admin granting a second
  // organization mid-session — while `/me` sits cached at `staleTime: Infinity`
  // and never re-asks. Its `details.candidates` travel with it.
  if (ACCOUNT_DENIAL_CODES.has(error.code) || error.code === "ORGANIZATION_SELECTION_REQUIRED") {
    reportSessionDenialViaBridge(error.code, error.message, envelope.details);
  }
  return error;
}

function contractViolation(response: Response, err: z.ZodError): ApiError {
  return new ApiError(0, {
    code: "CONTRACT_VIOLATION",
    message: `Response failed schema validation: ${err.message}`,
    request_id: response.headers.get("X-Request-Id") ?? "unknown",
    details: { issues: err.issues },
  });
}
